import React, { useEffect, useState } from "react";
import {
  Loader2, Shield, Monitor, Smartphone, Tablet,
  CheckCircle2, MoreVertical, LogOut, AlertTriangle, Clock,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { useI18n } from "../lib/i18n";
import { api } from "../lib/api";
import { toast } from "sonner";
import PushNotificationsCard from "../components/PushNotificationsCard";

function deviceIcon(device) {
  if (device === "Mobile") return Smartphone;
  if (device === "Tablet") return Tablet;
  return Monitor;
}

function relTime(iso, lang) {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  const diff = (Date.now() - t) / 1000;
  const tr = (ca, es) => (lang === "es" ? es : ca);
  if (diff < 60) return tr("fa uns segons", "hace unos segundos");
  if (diff < 3600) {
    const m = Math.floor(diff / 60);
    return tr(`fa ${m} min`, `hace ${m} min`);
  }
  if (diff < 86400) {
    const h = Math.floor(diff / 3600);
    return tr(`fa ${h}h`, `hace ${h}h`);
  }
  const d = Math.floor(diff / 86400);
  return tr(`fa ${d}d`, `hace ${d}d`);
}

export default function Security() {
  const { lang } = useI18n();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const tr = (ca, es) => (lang === "es" ? es : ca);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/auth/sessions");
      setSessions(r.data.sessions || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const revoke = async (jti) => {
    if (!window.confirm(tr(
      "Tancar aquesta sessió? L'usuari haurà de tornar a iniciar sessió en aquell dispositiu.",
      "¿Cerrar esta sesión? El usuario tendrá que volver a iniciar sesión en ese dispositivo."))) return;
    setBusy(jti);
    try {
      await api.post(`/auth/sessions/${jti}/revoke`);
      toast.success(tr("Sessió tancada", "Sesión cerrada"));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setBusy(null);
    }
  };

  const revokeOthers = async () => {
    if (!window.confirm(tr(
      "Tancar TOTES les altres sessions? Hauràs de tornar a iniciar sessió a la resta de dispositius.",
      "¿Cerrar TODAS las demás sesiones? Tendrás que volver a iniciar sesión en el resto de dispositivos."))) return;
    setBusy("all");
    try {
      const r = await api.post("/auth/sessions/revoke-others");
      toast.success(tr(
        `${r.data.revoked_count} sessions tancades`,
        `${r.data.revoked_count} sesiones cerradas`));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setBusy(null);
    }
  };

  const otherCount = sessions.filter(s => !s.current).length;

  return (
    <div className="space-y-6" data-testid="security-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-emerald-500 to-blue-500 flex items-center justify-center shadow-[0_0_24px_rgba(16,185,129,0.35)]">
            <Shield className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-bold tracking-tight text-white">
              {tr("Seguretat", "Seguridad")}
            </h1>
            <p className="text-xs text-slate-400 mt-0.5">
              {tr(
                "Gestiona les sessions actives al teu compte",
                "Gestiona las sesiones activas en tu cuenta"
              )}
            </p>
          </div>
        </div>
        {otherCount > 0 && (
          <Button
            onClick={revokeOthers}
            disabled={busy === "all"}
            variant="outline"
            className="bg-rose-500/10 border-rose-500/30 text-rose-200 hover:bg-rose-500/20"
            data-testid="security-revoke-others">
            {busy === "all" ? (
              <><Loader2 size={14} className="mr-2 animate-spin" />
              {tr("Tancant...", "Cerrando...")}</>
            ) : (
              <><AlertTriangle size={14} className="mr-2" />
              {tr(`Tancar les altres (${otherCount})`,
                  `Cerrar las demás (${otherCount})`)}</>
            )}
          </Button>
        )}
      </div>

      {/* Educational banner */}
      <div className="card-surface p-4 border-blue-500/20 bg-blue-500/5">
        <div className="flex items-start gap-3">
          <Shield size={16} className="text-blue-300 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-slate-300 leading-relaxed">
            {tr(
              "Si veus una sessió que no reconeixes, tanca-la immediatament i canvia la contrasenya. Quan canvies la contrasenya, totes les sessions es tanquen automàticament.",
              "Si ves una sesión que no reconoces, ciérrala inmediatamente y cambia la contraseña. Al cambiar la contraseña, todas las sesiones se cierran automáticamente."
            )}
          </div>
        </div>
      </div>

      {/* Push notifications */}
      <PushNotificationsCard />

      {/* Sessions list */}
      <div className="card-surface p-5">
        <div className="flex items-center gap-2 mb-4">
          <Clock size={14} className="text-slate-400" />
          <h2 className="text-xs uppercase tracking-wider text-slate-400 font-semibold">
            {tr("Sessions actives", "Sesiones activas")}
          </h2>
          <span className="text-xs text-slate-500 font-mono">
            ({sessions.length})
          </span>
        </div>

        {loading ? (
          <div className="py-12 text-center text-slate-400">
            <Loader2 className="w-5 h-5 mx-auto animate-spin" />
          </div>
        ) : sessions.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-8">
            {tr("Sense sessions actives", "Sin sesiones activas")}
          </p>
        ) : (
          <div className="space-y-2">
            {sessions.map((s) => {
              const Icon = deviceIcon(s.device);
              return (
                <div
                  key={s.jti}
                  className={`flex items-start gap-3 p-3.5 rounded-lg border ${
                    s.current
                      ? "bg-emerald-500/8 border-emerald-500/40 ring-1 ring-emerald-500/20"
                      : "bg-white/[0.02] border-white/5"
                  }`}
                  data-testid={`security-session-${s.jti}`}>
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
                    s.current ? "bg-emerald-500/15 text-emerald-300"
                              : "bg-white/5 text-slate-300"
                  }`}>
                    <Icon size={16} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-medium text-slate-200">
                        {s.browser}
                      </span>
                      <span className="text-xs text-slate-500">·</span>
                      <span className="text-xs text-slate-400">{s.os}</span>
                      {s.current && (
                        <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-200 border border-emerald-500/40 font-semibold"
                              data-testid="security-current-badge">
                          <CheckCircle2 size={10} />
                          {tr("Aquesta sessió", "Esta sesión")}
                        </span>
                      )}
                    </div>
                    <div className="text-[11px] text-slate-500 mt-1 font-mono">
                      IP: {s.ip || "—"}
                      <span className="mx-2 opacity-50">·</span>
                      {tr("Vist", "Visto")} {relTime(s.last_seen, lang)}
                      <span className="mx-2 opacity-50">·</span>
                      {tr("Iniciat", "Iniciado")} {relTime(s.created_at, lang)}
                    </div>
                  </div>
                  {!s.current && (
                    <Button
                      onClick={() => revoke(s.jti)}
                      disabled={busy === s.jti}
                      size="sm"
                      variant="outline"
                      className="bg-white/5 border-white/10 text-slate-300 hover:bg-rose-500/15 hover:border-rose-500/40 hover:text-rose-200 flex-shrink-0"
                      data-testid={`security-revoke-${s.jti}`}>
                      {busy === s.jti ? (
                        <Loader2 size={12} className="animate-spin" />
                      ) : (
                        <><LogOut size={12} className="mr-1.5" />
                        {tr("Tanca", "Cerrar")}</>
                      )}
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
